import React from 'react';
import { styled } from '@mui/material/styles';
import { CustomTileData } from 'features/layout/features/config/types';
import { hoverParentStyles } from 'features/layout/useHoverParent';
import { selectShowParticipantNamesInSystemSession } from 'features/layout/features/config/systemSessionSelectors';
import { SystemSessionGuard } from 'components/SystemSessionGuard';
import { StreamBadge } from 'features/streaming/components/stream-display/StreamBadge';
import { CustomTileControlButtons } from 'features/streaming/components/CustomTileControlButtons';
import { useIsCustomTileMaximized } from 'features/layout/tiles/useIsCustomTileMaximized';
import { useAppSelector } from 'store/hooks';
import { useSystemSession } from 'hooks/useSystemSession';

type CustomTileContentProps = {
  data: CustomTileData;
};

// The box is inline because a class first rendered inside PiP has no rules there; the hover rules
// stay in emotion since they need descendant selectors.
const ROOT_STYLE: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  borderRadius: '8px',
  overflow: 'hidden',
};

const HTML_CONTAINER_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

const Root = styled('div')(({ theme }) => ({
  padding: theme.spacing(4),
  '&:focus': {
    outline: 'none',
  },
  ...hoverParentStyles,
}));

// React only writes innerHTML again when the string itself changes, so the integrator's iframe
// survives every re-render for as long as this component stays mounted.
export const CustomTileContent = ({ data }: CustomTileContentProps) => {
  const showBadge = useAppSelector(selectShowParticipantNamesInSystemSession);

  const isSystemSession = useSystemSession();

  const maximized = useIsCustomTileMaximized(data.name);

  return (
    <Root style={ROOT_STYLE}>
      <SystemSessionGuard show={showBadge}>
        <StreamBadge
          label={data.name}
          className={isSystemSession ? 'hover-child-eager' : 'hover-child'}
        />
      </SystemSessionGuard>
      <SystemSessionGuard show>
        <CustomTileControlButtons data={data} collapseButtons={!maximized} />
      </SystemSessionGuard>
      <div
        id={`custom-tile-${data.name}`}
        style={HTML_CONTAINER_STYLE}
        // The markup is the integrator's, supplied through the SDK — carrying it verbatim is what
        // a custom tile is.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: data.html }}
      />
    </Root>
  );
};
